import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Room, RoomEvent, Track, RemoteTrack } from "livekit-client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStop,
  faXmark,
  faMicrophone,
  faMicrophoneSlash,
} from "@fortawesome/free-solid-svg-icons";
import { Loader2 } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "src/Store/store";
import { Button, Skeleton } from "@mui/material";
import MarkdownPreview from "@uiw/react-markdown-preview";
import { toast } from "react-toastify";
import ShifaraLogo from "../../assets/images/shifara-white-logo.png";
import askAiIcon from "../../assets/images/AskAi/ask-ai-logo-dark.png";
import AskAiBackground from "../../assets/images/AskAi/Ai-background.svg";

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

const API_CONFIG = {
  serverUrl: "https://api.heygen.com",
  token:
    process.env.REACT_APP_HEYGEN_TOKEN ||
    "NjQ5YTJiZWJkODM0NDU3YTg4YjNjZTE4MzFjMmZhMGYtMTc1OTk4NzY0MA==",
  avatarId:
    process.env.REACT_APP_HEYGEN_AVATOR_ID || "Dexter_Doctor_Standing2_public",
  voiceId:
    process.env.REACT_APP_HEYGEN_VOICE_ID || "df1b0eaaf0cb4229b238ec530828ea83",
};

function useAutoDuckOnSpeech(opts: {
  audioRef: React.RefObject<HTMLAudioElement>;
  isAvatarSpeaking: boolean;
  interruptConversation: () => void;
  getIsUserSpeaking: () => boolean;
  onResume?: () => void;
  duckVolume?: number;
  releaseMs?: number;
}) {
  const {
    audioRef,
    isAvatarSpeaking,
    interruptConversation,
    getIsUserSpeaking,
    onResume,
    duckVolume = 0.2,
    releaseMs = 500,
  } = opts;

  const lastStateRef = React.useRef<{ ducked: boolean; t: number }>({
    ducked: false,
    t: 0,
  });

  const rafRef = React.useRef<number | null>(null);

  const loop = React.useCallback(() => {
    const now = performance.now();
    const userSpeaking = getIsUserSpeaking();

    if (userSpeaking) {
      if (isAvatarSpeaking) {
        interruptConversation();
      }
      if (audioRef.current && !lastStateRef.current.ducked) {
        audioRef.current.volume = Math.min(duckVolume, audioRef.current.volume);
        lastStateRef.current = { ducked: true, t: now };
      } else {
        if (lastStateRef.current.ducked) lastStateRef.current.t = now;
      }
    } else {
      if (
        lastStateRef.current.ducked &&
        now - lastStateRef.current.t > releaseMs
      ) {
        if (audioRef.current) {
          const target = 1.0;
          const step = 0.1;
          const restore = () => {
            if (!audioRef.current) return;
            const v = Math.min(target, audioRef.current.volume + step);
            audioRef.current.volume = v;
            if (v < target && !getIsUserSpeaking()) {
              requestAnimationFrame(restore);
            } else {
              lastStateRef.current.ducked = false;
              onResume && onResume();
            }
          };
          restore();
        } else {
          lastStateRef.current.ducked = false;
          onResume && onResume();
        }
      }
    }

    rafRef.current = requestAnimationFrame(loop);
  }, [
    audioRef,
    isAvatarSpeaking,
    interruptConversation,
    getIsUserSpeaking,
    duckVolume,
    releaseMs,
    onResume,
  ]);

  React.useEffect(() => {
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [loop]);
}

const TalkingAvator: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { userDetails } = useSelector((state: RootState) => state.appReducer);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("en-US");
  const apiEndpoint = process.env.REACT_APP_AI_API_BASEURL;
  const [duration, setDuration] = useState(0);
  const [summary, setSummary] = useState<{
    session_id: string;
    summary: string;
  } | null>(null);
  const [summaryLoaded, setSummaryLoaded] = useState<boolean>(false);
  const [showSummary, setShowSummary] = useState(false);
  const [sessionId, setSessionId] = useState(
    Math.random().toString(36).substring(2, 15)
  );
  const [microphoneSensitivity, setMicrophoneSensitivity] = useState("medium");
  const [sessionLoading, setSessionLoading] = useState(false);
  const [voiceSpeed, setVoiceSpeed] = useState(1.3);
  const [audioVolume, setAudioVolume] = useState(0.55);
  const [voiceProvider, setVoiceProvider] = useState("azure");
  const [silenceTimeoutSeconds, setSilenceTimeoutSeconds] = useState(2);
  const [backgroundDenoisingEnabled, setBackgroundDenoisingEnabled] =
    useState(true);
  const [fillerInjectionEnabled, setFillerInjectionEnabled] = useState(false);
  const [micEnabled, setMicEnabled] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const audioLevelIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isAvatarSpeaking, setIsAvatarSpeaking] = useState(false);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTranscriptRef = useRef("");
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [startConsultation, setStartConsultation] = useState(false);
  const [sessionReconnecting, setSessionReconnecting] = useState(false);
  const [socketConflict, setSocketConflict] = useState(false);
  const [mainTime, setMainTime] = useState(0);
  const [silenceStartTime, setSilenceStartTime] = useState<number | null>(null);
  const [isStillThereMessageSent, setIsStillThereMessageSent] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [taskError, setTaskError] = useState<string | null>(null);
  const [taskTime, setTaskTime] = useState<
    { duration_ms: number; id: number }[] | null
  >([]);
  const [taskSending, setTaskSending] = useState(false);
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [audioAnalyser, setAudioAnalyser] = useState<AnalyserNode | null>(null);
  const [audioSource, setAudioSource] =
    useState<MediaStreamAudioSourceNode | null>(null);
  const [filteredAudioStream, setFilteredAudioStream] =
    useState<MediaStream | null>(null);
  const [isFilteringAudio, setIsFilteringAudio] = useState(false);
  const [vadEnabled, setVadEnabled] = useState(true);
  const [micSensitivityLevel, setMicSensitivityLevel] = useState<
    "high" | "medium" | "low"
  >("medium");
  const [dynamicSensitivity, setDynamicSensitivity] = useState(1.0);
  const [interruptionThreshold, setInterruptionThreshold] = useState(0.8);
  const [audioLevelHistory, setAudioLevelHistory] = useState<number[]>([]);
  const [volumeThreshold, setVolumeThreshold] = useState(0.4);
  const [maxVolumeThreshold, setMaxVolumeThreshold] = useState(0.8);
  const [volumeHistory, setVolumeHistory] = useState<number[]>([]);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [volumeCalibration, setVolumeCalibration] = useState<{
    min: number;
    max: number;
    avg: number;
  }>({ min: 0, max: 0, avg: 0 });
  const [micTemporarilyClosed, setMicTemporarilyClosed] = useState(false);
  const [humanVoiceDetected, setHumanVoiceDetected] = useState(false);
  const [originalAudioVolume, setOriginalAudioVolume] = useState(0.55);
  const [lastInterruptionTime, setLastInterruptionTime] = useState(0);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [pendingResponses, setPendingResponses] = useState<string[]>([]);
  const [lastUserMessage, setLastUserMessage] = useState<string>("");

  const mediaStream = useRef<MediaStream | null>(null);
  const roomRef = useRef<Room | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const recognitionRef = useRef<any | null>(null);

  useEffect(() => {
    if ("SpeechRecognition" in window || "webkitSpeechRecognition" in window) {
      const SpeechRecognition =
        window.SpeechRecognition || (window as any).webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = selectedLanguage;
    }
  }, [selectedLanguage]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (startConsultation) {
      interval = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(interval as unknown as NodeJS.Timeout);
      interval = null;
      setDuration(0);
    }
    return () => {
      if (interval) {
        clearInterval(interval as unknown as NodeJS.Timeout);
        interval = null;
      }
    };
  }, [startConsultation]);

  const createAudioFilter = async (originalStream: MediaStream) => {
    try {
      const ctx = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      setAudioContext(ctx);

      const source = ctx.createMediaStreamSource(originalStream);
      setAudioSource(source);

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 4096;
      analyser.smoothingTimeConstant = 0.9;
      setAudioAnalyser(analyser);

      const gainNode = ctx.createGain();

      const highpassFilter = ctx.createBiquadFilter();
      highpassFilter.type = "highpass";
      highpassFilter.frequency.value = 300;
      highpassFilter.Q.value = 0.5;

      const lowpassFilter = ctx.createBiquadFilter();
      lowpassFilter.type = "lowpass";
      lowpassFilter.frequency.value = 3000;
      lowpassFilter.Q.value = 0.5;

      const notchFilter1 = ctx.createBiquadFilter();
      notchFilter1.type = "notch";
      notchFilter1.frequency.value = 1000;
      notchFilter1.Q.value = 20;

      const notchFilter2 = ctx.createBiquadFilter();
      notchFilter2.type = "notch";
      notchFilter2.frequency.value = 2000;
      notchFilter2.Q.value = 15;

      const notchFilter3 = ctx.createBiquadFilter();
      notchFilter3.type = "notch";
      notchFilter3.frequency.value = 50;
      notchFilter3.Q.value = 5;

      const notchFilter4 = ctx.createBiquadFilter();
      notchFilter4.type = "notch";
      notchFilter4.frequency.value = 1500;
      notchFilter4.Q.value = 10;

      const notchFilter5 = ctx.createBiquadFilter();
      notchFilter5.type = "notch";
      notchFilter5.frequency.value = 2500;
      notchFilter5.Q.value = 10;

      const dynamicGain = ctx.createGain();
      dynamicGain.gain.value = isAvatarSpeaking ? 0.0 : 1.0;

      source.connect(highpassFilter);
      highpassFilter.connect(lowpassFilter);
      lowpassFilter.connect(notchFilter1);
      notchFilter1.connect(notchFilter2);
      notchFilter2.connect(notchFilter3);
      notchFilter3.connect(notchFilter4);
      notchFilter4.connect(notchFilter5);
      notchFilter5.connect(dynamicGain);
      dynamicGain.connect(gainNode);
      gainNode.connect(analyser);

      const destination = ctx.createMediaStreamDestination();
      gainNode.connect(destination);

      setFilteredAudioStream(destination.stream);
      setIsFilteringAudio(true);

      return destination.stream;
    } catch (error) {
      console.error("Error creating audio filter:", error);
      return originalStream;
    }
  };

  const detectAvatarVoice = (audioData: Uint8Array): boolean => {
    if (!audioAnalyser) return false;

    const bufferLength = audioAnalyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    audioAnalyser.getByteFrequencyData(dataArray);

    const lowFreq = dataArray.slice(0, bufferLength * 0.1);
    const midFreq = dataArray.slice(bufferLength * 0.1, bufferLength * 0.4);
    const highFreq = dataArray.slice(bufferLength * 0.4, bufferLength);

    const lowAvg = lowFreq.reduce((sum, val) => sum + val, 0) / lowFreq.length;
    const midAvg = midFreq.reduce((sum, val) => sum + val, 0) / midFreq.length;
    const highAvg =
      highFreq.reduce((sum, val) => sum + val, 0) / highFreq.length;

    const frequencyRatio = midAvg / (lowAvg + 1);
    const highFrequencyRatio = highAvg / (midAvg + 1);
    const totalEnergy = lowAvg + midAvg + highAvg;

    const isAvatarVoice =
      frequencyRatio > 4.0 &&
      highFrequencyRatio < 0.5 &&
      totalEnergy > 100 &&
      midAvg > 120;

    const aiVoiceSignature =
      midAvg > 130 && highAvg < 40 && lowAvg < 25 && totalEnergy > 120;

    const syntheticVoicePattern =
      midAvg > 100 && highAvg / (midAvg + 1) < 0.3 && totalEnergy > 80;

    const finalDetection =
      isAvatarVoice || aiVoiceSignature || syntheticVoicePattern;

    if (isAvatarSpeaking && (totalEnergy > 30 || midAvg > 60)) {
      return true;
    }

    return finalDetection;
  };

  const analyzeVolumeLevel = (audioData: Uint8Array): number => {
    if (!audioData || audioData.length === 0) return 0;

    let sum = 0;
    for (let i = 0; i < audioData.length; i++) {
      sum += audioData[i] * audioData[i];
    }
    const rms = Math.sqrt(sum / audioData.length);
    return rms / 255;
  };

  const isVolumeUserSpeech = (volume: number): boolean => {
    setVolumeHistory((prev) => {
      const newHistory = [...prev, volume].slice(-30);
      return newHistory;
    });

    const recentVolumes = volumeHistory.slice(-15);
    const avgRecentVolume =
      recentVolumes.length > 0
        ? recentVolumes.reduce((sum, vol) => sum + vol, 0) /
          recentVolumes.length
        : 0;

    const dynamicThreshold = isAvatarSpeaking
      ? Math.max(volumeThreshold * 1.2, avgRecentVolume * 1.5)
      : volumeThreshold * 1.1;

    const isUserSpeech =
      volume > dynamicThreshold &&
      volume < maxVolumeThreshold &&
      avgRecentVolume > dynamicThreshold * 0.7;

    return isUserSpeech;
  };

  const detectHumanVoice = (audioData: Uint8Array): boolean => {
    if (!audioAnalyser) return false;

    const bufferLength = audioAnalyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    audioAnalyser.getByteFrequencyData(dataArray);

    const lowFreq = dataArray.slice(0, bufferLength * 0.1);
    const midFreq = dataArray.slice(bufferLength * 0.1, bufferLength * 0.4);
    const highFreq = dataArray.slice(bufferLength * 0.4, bufferLength);

    const lowAvg = lowFreq.reduce((sum, val) => sum + val, 0) / lowFreq.length;
    const midAvg = midFreq.reduce((sum, val) => sum + val, 0) / midFreq.length;
    const highAvg =
      highFreq.reduce((sum, val) => sum + val, 0) / highFreq.length;

    const isHumanVoice =
      midAvg > lowAvg * 0.8 &&
      highAvg > midAvg * 0.3 &&
      lowAvg + midAvg + highAvg > 30;

    if (isHumanVoice) {
      setHumanVoiceDetected(true);
    }

    return isHumanVoice;
  };

  const calibrateVolumeLevels = () => {
    setVolumeCalibration({ min: 0, max: 0, avg: 0 });

    const calibrationData: number[] = [];
    let calibrationCount = 0;

    const calibrationInterval = setInterval(() => {
      if (audioAnalyser && calibrationCount < 50) {
        const dataArray = new Uint8Array(audioAnalyser.frequencyBinCount);
        audioAnalyser.getByteFrequencyData(dataArray);
        const volume = analyzeVolumeLevel(dataArray);
        calibrationData.push(volume);
        calibrationCount++;
      } else {
        clearInterval(calibrationInterval);

        if (calibrationData.length > 0) {
          const min = Math.min(...calibrationData);
          const max = Math.max(...calibrationData);
          const avg =
            calibrationData.reduce((sum, vol) => sum + vol, 0) /
            calibrationData.length;

          setVolumeCalibration({ min, max, avg });
          setVolumeThreshold(Math.max(avg * 1.5, 0.2));
        }
      }
    }, 100);
  };

  const cleanupAudioFilter = () => {
    try {
      if (audioContext && audioContext.state !== "closed") {
        audioContext.close();
      }
    } catch {
    } finally {
      setAudioContext(null);
    }

    if (audioSource) {
      try {
        audioSource.disconnect();
      } catch {
      } finally {
        setAudioSource(null);
      }
    }

    setAudioAnalyser(null);
    setFilteredAudioStream(null);
    setIsFilteringAudio(false);
  };

  useEffect(() => {
    if (micEnabled && (window as any).currentAudioStream) {
      audioLevelIntervalRef.current = setInterval(() => {
        let rawAudioLevel = 0;
        let isAvatarVoiceDetected = false;
        let isUserSpeakingDetected = false;
        let isHumanVoiceDetected = false;

        try {
          const streamToAnalyze =
            filteredAudioStream || (window as any).currentAudioStream;

          if (streamToAnalyze && audioAnalyser) {
            const dataArray = new Uint8Array(audioAnalyser.frequencyBinCount);
            audioAnalyser.getByteFrequencyData(dataArray);

            const volumeLevel = analyzeVolumeLevel(dataArray);
            isUserSpeakingDetected = isVolumeUserSpeech(volumeLevel);
            isAvatarVoiceDetected = detectAvatarVoice(dataArray);
            isHumanVoiceDetected = detectHumanVoice(dataArray);

            const average =
              dataArray.reduce((sum, value) => sum + value, 0) /
              dataArray.length;
            rawAudioLevel = (average / 255) * 100;

            if (isAvatarVoiceDetected && volumeLevel < volumeThreshold) {
              rawAudioLevel = rawAudioLevel * 0.05;
            } else if (isAvatarVoiceDetected) {
              rawAudioLevel = rawAudioLevel * 0.1;
            }

            setIsUserSpeaking(isUserSpeakingDetected);

            if (isHumanVoiceDetected && isAvatarSpeaking) {
              handleImmediateInterruption();
              return;
            }

            if (isUserSpeakingDetected && isAvatarSpeaking) {
              handleImmediateInterruption();
              return;
            }
          } else {
            rawAudioLevel = Math.random() * 100;
          }
        } catch {
          rawAudioLevel = Math.random() * 100;
        }

        const adjustedLevel = adjustMicrophoneSensitivity(rawAudioLevel);
        setAudioLevel(adjustedLevel);
      }, 100);
    } else {
      if (audioLevelIntervalRef.current) {
        clearInterval(audioLevelIntervalRef.current);
        audioLevelIntervalRef.current = null;
      }
      setAudioLevel(0);
      setAudioLevelHistory([]);
      setIsUserSpeaking(false);
    }

    return () => {
      if (audioLevelIntervalRef.current) {
        clearInterval(audioLevelIntervalRef.current);
      }
    };
  }, [
    micEnabled,
    isAvatarSpeaking,
    vadEnabled,
    dynamicSensitivity,
    filteredAudioStream,
    audioAnalyser,
    volumeThreshold,
    maxVolumeThreshold,
    taskTime,
  ]);

  const [sessionInfo, setSessionInfo] = useState<{
    roomId: string;
    mediaStream: MediaStream | null;
    info: any;
  }>({
    roomId: "",
    mediaStream: null,
    info: null,
  });

  const connectToLiveKit = async (sessionData: any) => {
    try {
      const room = new Room();
      roomRef.current = room;

      room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        if (track.kind === Track.Kind.Video) {
          handleVideoTrack(track);
        } else if (track.kind === Track.Kind.Audio) {
          handleAudioTrack(track);
        }
      });

      room.on(RoomEvent.Connected, () => {
        setIsConnected(true);
        setStartConsultation(true);
        startListening();
        setSilenceStartTime(Date.now());
      });

      room.on(RoomEvent.Disconnected, (reason) => {
        setIsConnected(false);
        setStartConsultation(false);
        stopListening();
        setIsListening(false);

        if ((window as any).currentAudioStream) {
          (window as any).currentAudioStream
            .getTracks()
            .forEach((track: any) => track.stop());
          (window as any).currentAudioStream = null;
        }

        if (
          reason &&
          (reason.toString().includes("CLIENT_INITIATED") ||
            reason.toString().includes("SERVER_ERROR"))
        ) {
          setSocketConflict(true);
        }
      });

      await room.connect(sessionData.url, sessionData.access_token);

      setIsConnected(true);

      const keepAliveInterval = setInterval(() => {
        if (room.state === "connected") {
          room.localParticipant.publishData(new TextEncoder().encode("ping"), {
            reliable: false,
          });
        }
      }, 5000);

      (room as any).keepAliveInterval = keepAliveInterval;
    } catch (error: any) {
      setSessionError(error.message || "Failed to connect to LiveKit");
    }
  };

  const handleVideoTrack = (track: RemoteTrack) => {
    if (videoRef.current && track.mediaStreamTrack) {
      const videoStream = new MediaStream([track.mediaStreamTrack]);
      videoRef.current.srcObject = videoStream;
      videoRef.current.play().catch(() => {});
    }
  };

  const handleAudioTrack = async (track: RemoteTrack) => {
    if (!track.mediaStreamTrack || !audioRef.current) return;

    const audioStream = new MediaStream([track.mediaStreamTrack]);
    audioRef.current.srcObject = audioStream;
    audioRef.current.volume = audioVolume;
    audioRef.current.play().catch(() => {});
  };

  const sendTextToAvatar = async (text: string) => {
    if (!sessionId) {
      setTaskError("Session not available");
      return;
    }

    setTaskSending(true);

    try {
      if (taskTime && taskTime.length > 0) {
        interruptConversation();
        setTaskTime([]);
      }

      const response = await fetch(
        `${API_CONFIG.serverUrl}/v1/streaming.task`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${API_CONFIG.token}`,
          },
          body: JSON.stringify({
            session_id: sessionId,
            text: text,
            task_type: "repeat",
            voice: getVoiceConfig(),
          }),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        setIsAvatarSpeaking(true);
        setOriginalAudioVolume(audioVolume);
        setMicTemporarilyClosed(true);

        // recognitionRef.current?.stop();

        let _id = responseData?.data?.task_id;
        let _duration_ms = responseData?.data?.duration_ms;
        setTaskTime((prev: any) => [
          ...prev,
          {
            duration_ms: _duration_ms,
            id: _id,
          },
        ]);
        setCurrentTranscript("");
        lastTranscriptRef.current = "";

        const interruptionCheck = setInterval(() => {
          setTaskTime((currentTaskTime) => {
            const taskStillExists =
              currentTaskTime &&
              currentTaskTime.some((task: any) => task.id === _id);
            if (!taskStillExists) {
              clearInterval(interruptionCheck);
            }
            return currentTaskTime;
          });
        }, 50);

        setTimeout(async () => {
          clearInterval(interruptionCheck);
          setTaskTime((prev: any) =>
            prev.filter((task: any) => task.id !== _id)
          );
          setIsAvatarSpeaking(false);
          setMicTemporarilyClosed(false);
          if (audioRef.current) {
            audioRef.current.volume = originalAudioVolume;
          }

          setTimeout(() => {
            if (micEnabled) {
              // recognitionRef.current?.start();
            }
          }, 500);

          setSilenceStartTime(Date.now());
        }, _duration_ms);
      }

      if (response.status === 400) {
        const errorData = await response.json();
        if (errorData.code === 10002) {
          setTaskError("Session closed. Attempting to reconnect...");
          return;
        }
      }

      if (!response.ok) {
        setTaskError(
          `Avatar API error: ${response.status} - ${response.statusText}`
        );
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error: any) {
      setTaskError(error.message || "Failed to send message to avatar");
    } finally {
      setMainTime(0);
      setTaskSending(false);
    }
  };

  const getVoiceConfig = () => {
    return {
      voice_id: API_CONFIG.voiceId,
      speed: voiceSpeed,
      provider: voiceProvider,
      filler_injection_enabled: fillerInjectionEnabled,
      background_denoising_enabled: backgroundDenoisingEnabled,
    };
  };

  const startSession = async () => {
    if (sessionLoading)
      return toast.error("Session is already creating Please wait...");
    setShowSummary(false);
    setSessionLoading(true);
    setSessionError(null);

    try {
      let response = await axios.post(
        `${API_CONFIG.serverUrl}/v1/streaming.new`,
        {
          version: "v2",
          avatar_id: API_CONFIG.avatarId,
          voice: {
            voice_id: API_CONFIG.voiceId,
            speed: 1.3,
            provider: "azure",
            filler_injection_enabled: false,
            background_denoising_enabled: true,
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${API_CONFIG.token}`,
          },
        }
      );

      let data = response.data.data;
      setSessionId(data.session_id);
      setSessionInfo((prev) => ({
        ...prev,
        info: data,
      }));

      await axios.post(
        `${API_CONFIG.serverUrl}/v1/streaming.start`,
        {
          session_id: data.session_id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${API_CONFIG.token}`,
          },
        }
      );

      if (data.session_id && data.access_token) {
        await connectToLiveKit(data);
      }
    } catch (error: any) {
      setSessionError(error.message || "Failed to start session");
    } finally {
      setSessionLoading(false);
    }
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  const adjustMicrophoneSensitivity = (level: number) => {
    if (!vadEnabled) return level;

    if (isAvatarSpeaking) {
      return level * 0.1;
    }

    return level * (dynamicSensitivity * 1.2);
  };

  const detectVoiceActivity = (audioLevel: number): boolean => {
    setAudioLevelHistory((prev) => {
      const newHistory = [...prev, audioLevel].slice(-20);
      return newHistory;
    });

    const avgLevel =
      audioLevelHistory.length > 0
        ? audioLevelHistory.reduce((sum, level) => sum + level, 0) /
          audioLevelHistory.length
        : audioLevel;

    const currentThreshold = isAvatarSpeaking ? 0.6 : 0.5;

    const isVoiceActive =
      audioLevel > currentThreshold &&
      avgLevel > currentThreshold * 0.8 &&
      audioLevelHistory.length >= 10;

    return isVoiceActive;
  };

  const handleImmediateInterruption = () => {
    const now = Date.now();
    if (now - lastInterruptionTime < 2000) {
      return;
    }

    if (isAvatarSpeaking || (taskTime && taskTime.length > 0)) {
      setLastInterruptionTime(now);
      interruptConversation();
      setIsAvatarSpeaking(false);
      setTaskTime([]);

      if (audioRef.current) {
        audioRef.current.volume = 0;
      }

      setPendingResponses([]);
      setMicTemporarilyClosed(false);
      setHumanVoiceDetected(false);
      setIsProcessing(false);

      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }

      setCurrentTranscript("");
      lastTranscriptRef.current = "";
      setMicEnabled(true);
      recognitionRef.current?.start();

      setSilenceStartTime(Date.now());
      setIsStillThereMessageSent(false);
      setAudioLevelHistory([]);
      setVolumeHistory([]);
    }
  };

  const isValidSpeech = (text: string) => {
    const filteredText = text.toLowerCase().trim();
    console.log("isValidSpeech: Checking text:", filteredText);

    const ignorePatterns = [
      "♪",
      "♫",
      "♬",
      "music",
      "song",
      "playing",
      "track",
      "album",
      "system",
      "notification",
      "alert",
      "beep",
      "ding",
      "chime",
      "tone",
      "sound",
      "are you still there",
      "i am your ai doctor",
      "based on your symptoms",
      "please describe your symptoms",
    ];

    const hasIgnorePattern = ignorePatterns.some((pattern) =>
      filteredText.includes(pattern)
    );
    console.log("isValidSpeech: hasIgnorePattern:", hasIgnorePattern);

    let minLength = 2; // Relaxed to allow short inputs like "Hello Helo"
    let allowRepeatedChars = true;

    switch (microphoneSensitivity) {
      case "low":
        minLength = 4;
        allowRepeatedChars = false;
        break;
      case "medium":
        minLength = 2;
        allowRepeatedChars = true;
        break;
      case "high":
        minLength = 1;
        allowRepeatedChars = true;
        break;
    }

    const isTooShort = filteredText.length < minLength;
    const hasRepeatedChars =
      !allowRepeatedChars && /(.)\1{2,}/.test(filteredText);

    // Relaxed avatar speech patterns to avoid filtering user speech
    const avatarSpeechPatterns = [
      /^i am your ai doctor/i,
      /^please describe your symptoms/i,
      /^based on your symptoms/i,
      /^are you still there/i,
    ];
    const isAvatarSpeech = avatarSpeechPatterns.some((pattern) =>
      pattern.test(filteredText)
    );
    console.log("isValidSpeech: isAvatarSpeech:", isAvatarSpeech);

    const isValid =
      !hasIgnorePattern && !isTooShort && !hasRepeatedChars && !isAvatarSpeech;
    console.log("isValidSpeech: Result:", isValid);

    return isValid;
  };

  useEffect(() => {
    if (!recognitionRef.current) return;

    recognitionRef.current.onresult = (event: any) => {
      let interimTranscript = "";
      let finalTranscript = "";

      console.log("onresult: Speech event received:", event);

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        console.log(
          `onresult: Transcript [${i}] (isFinal: ${event.results[i].isFinal}):`,
          transcript
        );
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      const currentText = finalTranscript || interimTranscript;
      console.log("onresult: Current text:", currentText);

      if (!currentText.trim()) {
        console.log("onresult: Empty transcript, ignoring");
        return;
      }

      if (isAvatarSpeaking) {
        const aiVoicePatterns = [
          /^i am your ai doctor/i,
          /^please describe your symptoms/i,
          /^based on your symptoms/i,
          /^are you still there/i,
        ];
        const isAIVoice = aiVoicePatterns.some((pattern) =>
          pattern.test(currentText.trim())
        );
        console.log("onresult: isAIVoice:", isAIVoice);
        if (isAIVoice) {
          console.log("onresult: Ignoring AI voice transcript");
          return;
        }
      }

      const isValid = isValidSpeech(currentText);
      console.log("onresult: isValidSpeech:", isValid);
      if (!isValid) {
        console.log("onresult: Invalid speech detected, ignoring");
        return;
      }

      setCurrentTranscript(currentText);
      lastTranscriptRef.current = currentText;
      setMainTime(0);

      setSilenceStartTime(null);
      setIsStillThereMessageSent(false);

      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }

      if (isAvatarSpeaking && taskTime && taskTime.length > 0) {
        console.log("onresult: Avatar speaking, delaying transcription");
        return;
      }

      if (finalTranscript.trim() && !isSendingMessage) {
        console.log("onresult: Setting silence timeout for final transcript");
        silenceTimeoutRef.current = setTimeout(() => {
          console.log("onresult: Silence timeout triggered, sending message");
          setIsSendingMessage(true);
          handleSendMessage(finalTranscript);
          setCurrentTranscript("");
          lastTranscriptRef.current = "";
        }, silenceTimeoutSeconds * 1000);
      } else {
        console.log(
          "onresult: Not sending message. finalTranscript:",
          finalTranscript,
          "isSendingMessage:",
          isSendingMessage
        );
      }
    };

    recognitionRef.current.onerror = (event: any) => {
      console.error("Speech recognition error:", event.error);
      if (event.error === "no-speech" || event.error === "aborted") {
        setTimeout(() => {
          if (micEnabled && !isAvatarSpeaking) {
            console.log("onerror: Restarting recognition");
            recognitionRef.current?.start();
          }
        }, 500);
      }
    };

    recognitionRef.current.onend = () => {
      console.log("onend: Speech recognition ended");
      setIsListening(false);
      if (micEnabled && !isAvatarSpeaking && !isProcessing) {
        setTimeout(() => {
          console.log("onend: Restarting recognition");
          recognitionRef.current?.start();
        }, 500);
      }
    };

    // return () => {
    //   console.log("cleanup: Stopping recognition");
    //   recognitionRef.current?.stop();
    // };
  }, [isAvatarSpeaking, isProcessing, isSendingMessage, selectedLanguage]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentTranscript, isProcessing]);

  const startListening = () => {
    if (
      !("SpeechRecognition" in window || "webkitSpeechRecognition" in window)
    ) {
      alert(
        "Your browser does not support speech recognition. Please use Chrome or Edge."
      );
      return;
    }
    if (isProcessing || isAvatarSpeaking) {
      setTimeout(() => {
        if (!isAvatarSpeaking && !isProcessing) startListening();
      }, 600);
      return;
    }

    setCurrentTranscript("");
    lastTranscriptRef.current = "";
    setMicEnabled(true);
    setMicTemporarilyClosed(false);
    setHumanVoiceDetected(false);
    setIsUserSpeaking(false);

    navigator.mediaDevices
      .getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 48000,
          sampleSize: 16,
          googEchoCancellation: true,
          googNoiseSuppression: true,
          googAutoGainControl: true,
          googHighpassFilter: true,
        },
      } as any)
      .then(async (stream) => {
        (window as any).currentAudioStream = stream;
        const filteredStream = await createAudioFilter(stream);
        console.log("startListening: Filtered stream created:", filteredStream);

        setTimeout(() => calibrateVolumeLevels(), 800);

        recognitionRef.current?.start();
        setIsListening(true);

        if (stream.getAudioTracks().length > 0) {
          const audioTrack = stream.getAudioTracks()[0];
          audioTrack
            .applyConstraints({
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            })
            .catch(() => {});
        }
      })
      .catch(() => {
        alert(
          "Error accessing microphone. Check permissions or if another app is using it."
        );
      });
  };

  const stopListening = () => {
    console.log("stopListening: Stopping recognition");
    recognitionRef.current?.stop();
    setMicEnabled(false);

    if ((window as any).currentAudioStream) {
      (window as any).currentAudioStream
        .getTracks()
        .forEach((track: any) => track.stop());
      (window as any).currentAudioStream = null;
    }

    cleanupAudioFilter();

    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }
  };

  const handleSendMessage = async (text: string) => {
    if (!text.trim()) {
      console.log("handleSendMessage: Empty text, ignoring");
      setIsSendingMessage(false);
      return;
    }

    console.log("handleSendMessage: Sending message:", text);

    setLastUserMessage(text.trim());
    setSilenceStartTime(null);
    setIsStillThereMessageSent(false);

    const userMessage: Message = {
      id: Date.now().toString(),
      text: text.trim(),
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setCurrentTranscript("");
    lastTranscriptRef.current = "";
    setIsProcessing(true);

    try {
      const response = await axios.post(
        `${apiEndpoint}/chat?session_id=${sessionId}`,
        {
          message: text.trim(),
          message_type: "user",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const aiResponse =
        response.data.response ||
        response.data.message ||
        "API Response received";
      let sesId = response.data.session_id;

      console.log("handleSendMessage: AI response:", aiResponse);

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponse,
        isUser: false,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);
      sendTextToAvatar(aiResponse);
      setSilenceStartTime(Date.now());
    } catch (error) {
      console.error("handleSendMessage: API error:", error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: "API call failed. Check your endpoint and try again.",
        isUser: false,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      setSilenceStartTime(Date.now());
    } finally {
      setIsProcessing(false);
      setIsSendingMessage(false);

      setTimeout(() => {
        setMicTemporarilyClosed(false);
        setHumanVoiceDetected(false);
        setIsUserSpeaking(false);
        setAudioLevelHistory([]);
        setVolumeHistory([]);
        if (micEnabled) {
          console.log("handleSendMessage: Restarting recognition post-message");
          recognitionRef.current?.start();
        }
      }, 500);
    }
  };

  const stopSession = async () => {
    if (!sessionInfo?.info?.session_id || !isConnected) return;
    try {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }

      if (sessionInfo?.info?.session_id && isConnected) {
        try {
          await axios.post(
            `${API_CONFIG.serverUrl}/v1/streaming.stop`,
            {
              session_id: sessionInfo.info.session_id,
            },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${API_CONFIG.token}`,
              },
            }
          );
          setTimeout(() => {
            getSummry(sessionId);
          }, 1000);
        } catch (error) {
          console.error("stopSession: Error stopping session:", error);
          setTimeout(() => {
            getSummry(sessionId);
          }, 1000);
        }
      }

      if (roomRef.current) {
        if ((roomRef.current as any).keepAliveInterval) {
          clearInterval((roomRef.current as any).keepAliveInterval);
        }
        stopListening();
        await roomRef.current.disconnect();
      }

      recognitionRef.current?.stop();
      setIsListening(false);

      if ((window as any).currentAudioStream) {
        (window as any).currentAudioStream
          .getTracks()
          .forEach((track: any) => track.stop());
        (window as any).currentAudioStream = null;
      }

      cleanupAudioFilter();

      setIsConnected(false);
      setIsProcessing(false);
      setTaskError("");
      setTaskSending(false);
      setMainTime(0);
      setSilenceStartTime(null);
      setIsStillThereMessageSent(false);
    } catch (error: any) {
      setSessionError(error.message || "Failed to stop session");
    }
  };

  useEffect(() => {
    if (silenceStartTime && isConnected && !isAvatarSpeaking) {
      const checkSilence = () => {
        if (!silenceStartTime) return;

        const silenceDuration = Date.now() - silenceStartTime;

        if (silenceDuration >= 20000 && !isStillThereMessageSent) {
          let name =
            userDetails?.firstName || userDetails?.lastName
              ? `${userDetails?.firstName || ""} ${userDetails?.lastName || ""}`
              : "User";
          let message = `Hello, ${name} are you still there?`;
          sendTextToAvatar(message);
          setIsStillThereMessageSent(true);
        }

        if (silenceDuration >= 30000) {
          stopSession();
        }
      };

      const interval = setInterval(checkSilence, 1000);
      return () => clearInterval(interval);
    }
  }, [
    silenceStartTime,
    isStillThereMessageSent,
    isConnected,
    isAvatarSpeaking,
  ]);

  useEffect(() => {
    return () => {
      console.log("Component cleanup: Stopping recognition and audio");
      recognitionRef.current?.stop();
      setIsListening(false);

      if ((window as any).currentAudioStream) {
        (window as any).currentAudioStream
          .getTracks()
          .forEach((track: any) => track.stop());
        (window as any).currentAudioStream = null;
      }

      cleanupAudioFilter();
      setAudioLevelHistory([]);
      setDynamicSensitivity(1.0);
    };
  }, [isConnected]);

  const interruptConversation = React.useCallback(() => {
    if (sessionInfo?.info?.session_id) {
      axios
        .post(
          `${API_CONFIG.serverUrl}/v1/streaming.interrupt`,
          {
            session_id: sessionInfo.info.session_id,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${API_CONFIG.token}`,
            },
          }
        )
        .then(() => {})
        .catch(() => {});
    }
  }, [sessionInfo?.info?.session_id]);

  const getIsUserSpeaking = React.useCallback(
    () => isUserSpeaking,
    [isUserSpeaking]
  );

  const getSummry = async (session_id: string) => {
    if (summaryLoaded || !session_id || !apiEndpoint) {
      return;
    }

    try {
      setSummaryLoaded(true);
      setShowSummary(true);

      const response = await axios.post(
        `${apiEndpoint}/chat/summary/${session_id}`,
        {},
        {
          timeout: 60000,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.summary) {
        setSummary({ session_id, summary: response.data.summary });
      } else {
        setSummary({
          session_id,
          summary: "No summary available for this session.",
        });
      }
    } catch (error: any) {
      console.error("getSummry: Error fetching summary:", error);
      let errorMessage =
        "Unable to generate summary. The session has ended successfully.";

      if (error.response?.status === 404) {
        errorMessage =
          "Summary service is not available. The session has ended successfully.";
      } else if (error.response?.status === 500) {
        errorMessage =
          "Summary generation failed. The session has ended successfully.";
      } else if (error.code === "ECONNABORTED") {
        errorMessage =
          "Summary request timed out. The session has ended successfully.";
      }

      setSummary({
        session_id,
        summary: errorMessage,
      });
    } finally {
      setSummaryLoaded(false);
    }
  };

  const toogleMic = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  useAutoDuckOnSpeech({
    audioRef,
    isAvatarSpeaking,
    interruptConversation,
    getIsUserSpeaking: () => isUserSpeaking,
    duckVolume: 0.2,
    releaseMs: 500,
  });

  return !(
    "SpeechRecognition" in window || "webkitSpeechRecognition" in window
  ) ? (
    <div className="flex items-center justify-center h-screen bg-gray-100">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-red-600 mb-4">
          Browser Not Supported
        </h2>
        <p className="text-gray-600">
          Your browser doesn't support speech recognition.
        </p>
      </div>
    </div>
  ) : (
    <div className="w-full h-full flex flex-col theme-gradient-green-bottom rounded-lg lg:rounded-[1vw] overflow-hidden">
      <div className="w-full p-2 lg:p-[0.7vw] !pb-0">
        <div className="flex items-center justify-between bg-[#f9db72] rounded-md lg:rounded-[0.3vw] py-2.5 lg:py-[0.4vw] px-3 lg:px-[0.5vw]">
          <h3 className="text-[#00443F] flex items-center gap-2 lg:gap-[0.5vw] font-size-20px font-Poppins-Medium">
            <img
              src={askAiIcon}
              alt="Ask Ai"
              className="w-[20px] h-[20px] lg:w-[1.3vw] lg:h-[1.3vw]"
            />{" "}
            Ask AI
          </h3>
          <button
            className="w-[20px] h-[20px] lg:w-[1.5vw] lg:h-[1.5vw] flex items-center justify-center theme-gradient-green-bottom rounded-full text-white cursor-pointer"
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faXmark} className="font-size-15px" />
          </button>
        </div>
      </div>

      <div className="flex-1 flex relative">
        {!showSummary && (
          <>
            {(!startConsultation || !isConnected) && (
              <div className="w-full h-full flex items-center gap-2 justify-center absolute top-0 left-0 right-0 bottom-0">
                <div className="w-[57%] h-full">
                  <img
                    src={AskAiBackground}
                    alt=""
                    className="h-full translate-x-[-13%]"
                    style={{ mixBlendMode: "lighten" }}
                  />
                </div>
                <div className="w-[43%] h-full flex flex-col justify-center">
                  <img
                    src={ShifaraLogo}
                    alt="Shifara AI"
                    className="w-[100px] lg:w-[10vw] object-contain"
                  />
                  <h3 className="font-size-65px font-Lexend-Bold mt-5 lg:mt-[1.2vw] leading-none text-white">
                    Talk to Our AI Doctor Now
                  </h3>
                  <p className="font-size-20px font-Poppins-Regular mt-3 lg:mt-[0.9vw] max-w-[600px] w-[90%] text-white">
                    Describe your symptoms and get instant guidance in simple,
                    clear language — no waiting rooms needed.
                  </p>
                  <button
                    disabled={sessionLoading}
                    onClick={startSession}
                    className="font-size-18px disabled:opacity-80 font-Poppins-SemiBold flex items-center gap-2 w-fit rounded-full text-[#3c3c3c] bg-white  px-4 py-2 lg:px-[1vw] lg:py-[0.5vw] mt-3 lg:mt-[0.9vw]"
                  >
                    Start Consultation{" "}
                    {sessionLoading && (
                      <Loader2 className="w-4 h-4 animate-spin lg:w-[1.2vw] lg:h-[1.2vw]" />
                    )}
                  </button>
                </div>
              </div>
            )}
            <div
              className={`w-full h-full overflow-hidden p-2 lg:p-[0.7vw] ${
                startConsultation && isConnected ? "" : "hidden"
              }`}
            >
              <div className="relative w-full h-full flex-1 overflow-hidden border border-gray-300 rounded-md lg:rounded-[0.3vw]">
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover rounded-md lg:rounded-[0.3vw] absolute top-0 left-0 right-0 bottom-0"
                  style={{
                    objectFit: "cover",
                    objectPosition: "top",
                  }}
                  autoPlay
                  muted
                  playsInline
                />
                <audio ref={audioRef} autoPlay playsInline controls={false} />
                <div className="absolute bottom-0 right-0 px-5 lg:px-[1.3vw] py-3 lg:py-[0.5vw] rounded-tl-lg lg:rounded-tl-[1vw] border-t border-l border-[#ccc] bg-[#f9dc72dc] text-white flex items-center gap-2">
                  <p>Powered by</p>
                  <img
                    src={ShifaraLogo}
                    alt="Shifara AI"
                    className="w-[100px] lg:w-[10vw] object-contain"
                  />
                </div>
                <button
                  className="absolute bottom-2 lg:bottom-[0.5vw] left-1/2 -translate-x-1/2 px-5 lg:px-[1.3vw] py-3 lg:py-[0.5vw] rounded-md lg:rounded-[0.3vw] bg-red-500 text-white flex items-center gap-2"
                  onClick={stopSession}
                >
                  <FontAwesomeIcon icon={faStop} />
                  <p>End Consultation</p>
                </button>
              </div>
            </div>
          </>
        )}
        {showSummary && (
          <div className="w-full min-h-full flex p-2 lg:p-[0.7vw] absolute top-0 left-0 right-0 bottom-0">
            <div className="flex flex-1 flex-col gap-2 z-20 relative p-2 lg:p-[1vw] overflow-hidden transition-all duration-300 rounded-lg border border-gray-400">
              <div className="flex items-center justify-between gap-2 pb-2 lg:pb-[1vw] border-b border-gray-300">
                <h3 className="text-white font-size-20px font-Poppins-Medium">
                  Summary of the conversation
                </h3>
                <div className="flex items-center gap-2">
                  <Button
                    variant="contained"
                    color={"info"}
                    onClick={() => {
                      startSession();
                    }}
                    className="font-size-16px font-Poppins-SemiBold text-[#00443F]"
                    sx={{
                      textTransform: "none",
                      backgroundColor: "#00443F",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#00400f",
                        color: "#fff",
                      },
                    }}
                  >
                    Start New Consultation
                  </Button>
                </div>
              </div>
              <div
                className="flex-1 overflow-y-auto p-3 space-y-2 chat-scroll"
                style={{ maxHeight: "calc(100vh - 200px)" }}
              >
                {!summaryLoaded ? (
                  <MarkdownPreview
                    source={summary?.summary || ""}
                    className="w-full bg-transparent text-[#eee] prose prose-sm max-w-none"
                  />
                ) : (
                  <div className="flex flex-col gap-2">
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height="25px"
                    />
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height="25px"
                    />
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height="25px"
                    />
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height="25px"
                    />
                    <Skeleton variant="rectangular" width="80%" height="25px" />
                    <Skeleton variant="rectangular" width="40%" height="25px" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0% { transform: scaleY(0.3); }
          100% { transform: scaleY(1); }
        }

        .chat-scroll {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        .chat-scroll::-webkit-scrollbar {
          width: 0px;
          background: transparent;
        }

        .chat-scroll:hover::-webkit-scrollbar {
          width: 6px;
        }

        .chat-scroll:hover::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 3px;
        }

        .chat-scroll:hover::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 3px;
        }

        .chat-scroll:hover::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }
      `}</style>
    </div>
  );
};

export default TalkingAvator;